import React, {useState} from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  ScrollView,
  Dimensions,
  StyleSheet,
} from 'react-native';
import {AntDesign} from '@expo/vector-icons';
import {noticeStyles, noticeTones} from './boxChampNoticeStyles';
import modalStyles from '../../style/boxChampCalculateModal';

const REASON_LABELS = {
  oversize: 'too large',
  overweight: 'too heavy',
};

const BoxChampOversizeWarning = ({warnings}) => {
  const [modalVisible, setModalVisible] = useState(false);
  if (!Array.isArray(warnings) || warnings.length === 0) return null;

  const count = warnings.length;
  const itemsOnly = warnings.every(w => w.product_name || w.sku);
  const bannerText = itemsOnly
    ? `⚠ ${count} item${count > 1 ? 's' : ''} won't fit a box — pack manually`
    : '⚠ Box suggestions unavailable — view details';
  const bannerAction = itemsOnly ? 'View items' : 'View details';
  const modalTitle = itemsOnly
    ? `Items that won't fit a box — ${count}`
    : `Box calculation problems — ${count}`;
  const windowWidth = Dimensions.get('window').width;
  const open = () => setModalVisible(true);
  const close = () => setModalVisible(false);

  return (
    <View>
      {/* Trigger banner — non-blocking; opens the item list. */}
      <TouchableOpacity
        testID="boxchamp-unboxed-warning"
        onPress={open}
        style={[noticeStyles.banner, styles.banner, {backgroundColor: noticeTones.danger.background}]}>
        <Text style={[noticeStyles.text, {color: noticeTones.danger.text}]}>
          {bannerText}
        </Text>
        <Text style={[styles.bannerAction, {color: noticeTones.danger.text}]}>{bannerAction}</Text>
      </TouchableOpacity>

      <Modal
        transparent
        visible={modalVisible}
        animationType="fade"
        onRequestClose={close}>
        <View style={modalStyles.overlay}>
          <View
            testID="boxchamp-unboxed-modal"
            style={[modalStyles.dialog, {width: windowWidth >= 900 ? '55%' : '95%'}]}>
            {/* Header */}
            <View style={modalStyles.header}>
              <Text style={modalStyles.headerTitle}>
                {modalTitle}
              </Text>
              <TouchableOpacity
                testID="boxchamp-unboxed-close"
                onPress={close}
                style={modalStyles.closeIcon}>
                <AntDesign name="close" size={20} color="#E0E7EF" />
              </TouchableOpacity>
            </View>

            {/* Body — scrollable list, bounded height regardless of item count */}
            <View style={modalStyles.body}>
              <ScrollView style={modalStyles.scrollArea}>
                {warnings.map((w, index) => (
                  <View
                    key={index}
                    testID={`boxchamp-unboxed-row-${index}`}
                    style={styles.row}>
                    {w.product_name || w.sku ? (
                      <>
                        <Text style={styles.rowLabel}>{w.product_name || w.sku}</Text>
                        <Text style={styles.rowReason}>
                          {REASON_LABELS[w.reason] || w.reason}
                        </Text>
                      </>
                    ) : (
                      <Text style={[styles.rowLabel, styles.rowReasonOnly]}>
                        {REASON_LABELS[w.reason] || w.reason}
                      </Text>
                    )}
                  </View>
                ))}
              </ScrollView>
            </View>

            {/* Footer — informational only; nothing to save */}
            <View style={modalStyles.footer}>
              <TouchableOpacity
                testID="boxchamp-unboxed-close-btn"
                onPress={close}
                style={modalStyles.cancelButton}>
                <Text style={modalStyles.cancelButtonText}>Close</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

export default BoxChampOversizeWarning;

const styles = StyleSheet.create({
  banner: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between'},
  bannerAction: {fontSize: 14, fontFamily: 'Poppins_600SemiBold', textDecorationLine: 'underline'},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#253c57',
  },
  rowLabel: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 13,
    color: '#E0E7EF',
    flex: 1,
    paddingRight: 8,
  },
  rowReason: {fontFamily: 'Poppins_500Medium', fontSize: 12, color: '#f1a1a8'},
  rowReasonOnly: {color: '#f1a1a8', paddingRight: 0},
});
